"use client";

import {
  Search,
  Filter,
  RefreshCw,
  ArrowDownUp,
  LayoutGrid,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { TextScramble } from "@/components/ui/effects/text-scramble";

export interface SearchBarConfig {
  showFilters?: boolean;
  showSort?: boolean;
  showGrid?: boolean;
  placeholderText?: string;
  sortOptions?: {
    id: string;
    label: string;
  }[];
  gridOptions?: {
    id: string;
    label: string;
  }[];
}

export interface SearchBarProps {
  onChange: (value: string) => void;
  onReset?: () => void;
  filteredCount?: number;
  totalCount?: number;
  onToggleFilters?: () => void;
  isFiltersOpen?: boolean;
  onSortToggle?: (e: React.MouseEvent) => void;
  isSortOpen?: boolean;
  sortOption?: string;
  onSortChange?: (option: string) => void;
  onGridToggle?: (e: React.MouseEvent) => void;
  isGridOpen?: boolean;
  onGridChange?: (option: string) => void;
  gridOption?: string;
  config?: SearchBarConfig;
  isFetchingNextPage?: boolean;
  hasMore?: boolean;
}

// Default configuration
const defaultConfig: SearchBarConfig = {
  showFilters: true,
  showSort: true,
  showGrid: true,
  placeholderText: "SEARCH...",
  sortOptions: [
    { id: "rand_asc", label: "RANDOM" },
    { id: "newest", label: "NEWEST" },
    { id: "oldest", label: "OLDEST" },
    { id: "name_asc", label: "A-Z" },
    { id: "name_desc", label: "Z-A" },
  ],
  gridOptions: [
    { id: "grid4", label: "4-COLUMN" },
    { id: "grid6", label: "6-COLUMN" },
    { id: "grid8", label: "8-COLUMN" },
    { id: "table", label: "TABLE" },
    { id: "gallery", label: "GALLERY" },
  ],
};

export function SearchBar({
  onChange,
  onReset,
  filteredCount,
  totalCount,
  onToggleFilters,
  isFiltersOpen = false,
  onSortToggle,
  isSortOpen = false,
  sortOption = "newest",
  onSortChange,
  onGridToggle,
  isGridOpen = false,
  onGridChange,
  gridOption = "grid6",
  config = defaultConfig,
  isFetchingNextPage,
  hasMore,
}: SearchBarProps) {
  const [searchValue, setSearchValue] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [_isInputFocused, setIsInputFocused] = useState(false);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  // Merge with default config
  const mergedConfig = { ...defaultConfig, ...config };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onChange(value);
  };

  const handleReset = () => {
    if (isAnimating) return; // Prevent multiple clicks during animation

    setIsAnimating(true);
    setSearchValue("");
    onChange("");
    if (onReset) onReset();

    // Reset animation state after it completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };

  const handleFocus = () => {
    setIsInputFocused(true);
  };

  const handleBlur = () => {
    setIsInputFocused(false);
  };

  // Add hover animation to reset button
  useEffect(() => {
    const button = resetButtonRef.current;
    if (!button) return undefined;

    const handleHover = () => {
      if (!isAnimating) {
        button.classList.add("scale-110");
      }
    };

    const handleLeave = () => {
      button.classList.remove("scale-110");
    };

    button.addEventListener("mouseenter", handleHover);
    button.addEventListener("mouseleave", handleLeave);

    return () => {
      button.removeEventListener("mouseenter", handleHover);
      button.removeEventListener("mouseleave", handleLeave);
    };
  }, [isAnimating]);

  return (
    <div className="flex flex-col w-full relative z-50">
      <div className="flex items-center gap-[1px]">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-black/70" />
          </div>
          <input
            type="text"
            // placeholder={isInputFocused ? "" : mergedConfig.placeholderText}
            value={searchValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="w-full h-10 pl-10 pr-[80px] md:pr-4 py-2 text-[14px]
             bg-white text-black border border-zinc-200 rounded-md font-mono focus:outline-hidden focus:border-zinc-300 uppercase placeholder-black/50"
          />
          {/* isInputFocused && */}
          { searchValue === "" && (
            <div className="absolute inset-y-0 left-0 pl-10 flex items-center pointer-events-none">
              <div
                ref={placeholderRef}
                className="text-[14px] text-black/50 font-mono uppercase"
              >
                <TextScramble
                  text={mergedConfig.placeholderText || "SEARCH..."}
                />
              </div>
            </div>
          )}
          {filteredCount !== undefined && totalCount !== undefined && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-[12px] font-mono text-black/70 font-semibold">
                {isFetchingNextPage ? (
                  <>
                    <span className="inline-flex items-center gap-1">
                      <span className="animate-pulse">···</span>
                      <span className="hidden md:inline">LOADING</span> [{filteredCount}/{totalCount}]
                    </span>
                  </>
                ) : !hasMore && filteredCount > 0 ? (
                  <>
                    <span className="hidden md:inline">ALL LOADED</span> [{filteredCount}/{totalCount}]
                  </>
                ) : (
                  <>
                    <span className="hidden md:inline">SHOWING</span> [{filteredCount}/{totalCount}]
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        {mergedConfig.showFilters && onToggleFilters && (
          <button
            onClick={onToggleFilters}
            className={`h-10 w-10 ml-[1px] flex items-center justify-center text-[12px] uppercase font-mono text-black rounded-md border border-zinc-200 transition-all duration-300 ease-in-out ${
              isFiltersOpen ? 'bg-zinc-300 text-black' : 'bg-white hover:bg-zinc-100'
            }`}
            data-filter-button
            title="Toggle filters"
          >
            <Filter className={`h-4 w-4 ${isFiltersOpen ? 'text-black' : 'text-zinc-700'}`} />
          </button>
        )}

        {mergedConfig.showSort && onSortToggle && (
          <button
            onClick={onSortToggle}
            className={`h-10 w-10 ml-[1px] flex items-center justify-center text-[12px] uppercase font-mono text-black rounded-md border border-zinc-200 transition-all duration-300 ease-in-out ${
              isSortOpen ? 'bg-zinc-300 text-black' : 'bg-white hover:bg-zinc-100'
            }`}
            title="Sort options"
            data-sort-button
          >
            <ArrowDownUp className={`h-4 w-4 ${isSortOpen ? 'text-black' : 'text-zinc-700'}`} />
          </button>
        )}

        {mergedConfig.showGrid && onGridToggle && (
          <button
            onClick={onGridToggle}
            className={`h-10 w-10 ml-[1px] flex items-center justify-center text-[12px] uppercase font-mono text-black rounded-md border border-zinc-200 transition-all duration-300 ease-in-out ${
              isGridOpen ? 'bg-zinc-300 text-black' : 'bg-white hover:bg-zinc-100'
            }`}
            title="Grid options"
            data-grid-button
          >
            <LayoutGrid className={`h-4 w-4 ${isGridOpen ? 'text-black' : 'text-zinc-700'}`} />
          </button>
        )}

        <button
          ref={resetButtonRef}
          onClick={handleReset}
          className={`h-10 w-10 ml-[1px] flex items-center justify-center text-[12px] uppercase font-mono border border-zinc-200 text-black rounded-md 
          ${isAnimating ? "bg-zinc-200" : "bg-white hover:bg-zinc-100"} 
          transition-all duration-300 ease-in-out transform relative`}
          aria-label="Reset all filters"
          disabled={isAnimating}
        >
          <RefreshCw
            className={`h-4 w-4 transition-all duration-500 ease-in-out 
            ${isAnimating ? "animate-rotate-360 text-black" : "text-zinc-700"}`}
          />
        </button>
      </div>

      {/* Sort Options PANEL */}
      {mergedConfig.showSort && onSortChange && (
        <div
          className="absolute bottom-full mb-1 w-full px-[5px] py-2 bg-white border-b border-zinc-100 transition-all duration-500 z-10"
          style={{
            opacity: isSortOpen ? 1 : 0,
            transform: isSortOpen ? "translateY(0)" : "translateY(10px)",
            pointerEvents: isSortOpen ? "auto" : "none",
            maxHeight: isSortOpen ? "1000px" : "0",
            overflow: "hidden",
          }}
        >
          <div className="flex justify-between items-center">
            <div className="text-[12px] font-mono text-black px-3 py-1.5 rounded-md">
              SORT ARTWORKS
            </div>
            <div className="flex justify-end gap-2 px-2 py-1">
              {mergedConfig.sortOptions?.map((option) => (
                <button
                  key={option.id}
                  className={`px-3 py-1.5 text-[12px] font-mono rounded-md ${
                    sortOption === option.id
                      ? "bg-black text-white"
                      : "bg-zinc-100 text-black hover:bg-zinc-200"
                  }`}
                  onClick={() => onSortChange(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grid Options PANEL */}
      {mergedConfig.showGrid && onGridChange && (
        <div
          className="absolute bottom-full mb-1 w-full px-[5px] py-2 bg-white border-b border-zinc-100 transition-all duration-500 z-10"
          style={{
            opacity: isGridOpen ? 1 : 0,
            transform: isGridOpen ? "translateY(0)" : "translateY(10px)",
            pointerEvents: isGridOpen ? "auto" : "none",
            maxHeight: isGridOpen ? "1000px" : "0",
            overflow: "hidden",
          }}
        >
          <div className="flex justify-between items-center">
            <div className="text-[12px] font-mono text-black px-3 py-1.5 rounded-md">
              LAYOUT TYPE
            </div>
            <div className="flex justify-end gap-2 px-2 py-1">
              {mergedConfig.gridOptions?.map((option) => (
                <button
                  key={option.id}
                  className={`px-3 py-1.5 text-[12px] font-mono rounded-md ${
                    gridOption === option.id
                      ? "bg-black text-white"
                      : "bg-zinc-100 text-black hover:bg-zinc-200"
                  }`}
                  onClick={() => onGridChange(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
